import { z } from 'zod';
import { idSchema } from './common.js';

/**
 * `pending` → el presign se emitió. `uploaded` → los bytes están y hay evento en el outbox.
 * `ready` → el worker husmeó, re-codificó y generó miniatura. `rejected` → no se sirve nunca.
 */
export const ATTACHMENT_STATUSES = ['pending', 'uploaded', 'ready', 'rejected'] as const;
export type AttachmentStatus = (typeof ATTACHMENT_STATUSES)[number];

/**
 * Solo estos tipos se sirven en línea, y solo después de que el worker los re-codifique.
 * Todo lo demás va como descarga: un SVG en línea es JavaScript ejecutable (diseño §9).
 */
export const INLINE_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'] as const;

export const isInlineImage = (mime: string | null): boolean =>
  mime !== null && (INLINE_IMAGE_TYPES as readonly string[]).includes(mime);

export const presignRequestSchema = z
  .object({
    id: idSchema,
    name: z.string().min(1).max(255),
    mime: z.string().min(1).max(128),
    size: z.number().int().positive(),
  })
  .strict();
export type PresignRequest = z.infer<typeof presignRequestSchema>;

export const presignResponseSchema = z
  .object({
    attachmentId: idSchema,
    uploadUrl: z.url(),
    expiresInSeconds: z.number().int().positive(),
  })
  .strict();
export type PresignResponse = z.infer<typeof presignResponseSchema>;

export const attachmentUrlsRequestSchema = z
  .object({ ids: z.array(idSchema).min(1).max(100) })
  .strict();

export const attachmentViewSchema = z
  .object({
    id: idSchema,
    name: z.string(),
    mime: z.string().nullable(),
    size: z.number().int(),
    status: z.enum(ATTACHMENT_STATUSES),
    width: z.number().int().nullable(),
    height: z.number().int().nullable(),
    hasThumb: z.boolean(),
  })
  .strict();
export type AttachmentView = z.infer<typeof attachmentViewSchema>;

export const signedUrlSchema = z
  .object({
    id: idSchema,
    url: z.url(),
    thumbUrl: z.url().nullable(),
    expiresAt: z.iso.datetime(),
  })
  .strict();
export type SignedUrl = z.infer<typeof signedUrlSchema>;

export const attachmentUrlsResponseSchema = z.object({ urls: z.array(signedUrlSchema) }).strict();

/** Vida de las urls firmadas. El cliente refresca un minuto antes para no mostrar un 403. */
export const SIGNED_URL_TTL_SECONDS = 900;
export const UPLOAD_URL_TTL_SECONDS = 900;
